/**
 * The only way this app puts anything on the screen.
 *
 * There is no `innerHTML` in this file and there is none anywhere else in `src/`
 * (`scripts/checks.mjs` greps for it and fails the build). Payee names, biller
 * names, references, transcripts and every model-authored string are
 * customer-, third-party- or attacker-authored: they are set with `textContent`,
 * which cannot produce an element. SVG icons are built with `createElementNS`
 * for the same reason — a markup string is a markup string.
 */

type Child = Node | string | null | undefined | false;

export interface Attrs {
  readonly class?: string;
  readonly id?: string;
  readonly type?: string;
  readonly role?: string;
  readonly hidden?: boolean;
  readonly disabled?: boolean;
  readonly tabIndex?: number;
  readonly title?: string;
  readonly value?: string;
  readonly placeholder?: string;
  readonly href?: string;
  readonly for?: string;
  readonly name?: string;
  readonly scope?: string;
  /** A table cell across columns (DCTA-188: judge mode's two-pass table). */
  readonly colspan?: string;
  readonly open?: boolean;
  readonly inputMode?: string;
  readonly autocomplete?: string;
  readonly maxLength?: number;
  // No `style` (F-CSP-3, docs/specs/csp-fe-worklet.md): the page's Content-Security-Policy refuses a style attribute.
  // A styled element is made with a class, or with `node.style` / `style.setProperty` once it exists.
  readonly data?: Readonly<Record<string, string>>;
  readonly aria?: Readonly<Record<string, string>>;
  readonly on?: Readonly<Record<string, EventListener>>;
}

const BOOLEAN_PROPS = new Set(["hidden", "disabled", "open"]);

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Attrs = {},
  children: readonly Child[] = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [key, value] of Object.entries(attrs)) {
    if (value === undefined || value === null) continue;
    if (key === "style") throw new TypeError("el(): no style attribute — the page's CSP refuses inline style; use a class or node.style");
    if (key === "data") {
      for (const [k, v] of Object.entries(value as Record<string, string>)) {
        node.setAttribute(`data-${k}`, v);
      }
    } else if (key === "aria") {
      for (const [k, v] of Object.entries(value as Record<string, string>)) {
        node.setAttribute(k === "label" || k.startsWith("aria-") ? ariaName(k) : ariaName(k), v);
      }
    } else if (key === "on") {
      for (const [k, v] of Object.entries(value as Record<string, EventListener>)) {
        node.addEventListener(k, v);
      }
    } else if (key === "class") {
      node.setAttribute("class", String(value));
    } else if (BOOLEAN_PROPS.has(key)) {
      if (value === true) node.setAttribute(key, "");
    } else if (key === "tabIndex") {
      node.setAttribute("tabindex", String(value));
    } else if (key === "inputMode") {
      node.setAttribute("inputmode", String(value));
    } else if (key === "maxLength") {
      node.setAttribute("maxlength", String(value));
    } else if (key === "value" && node instanceof HTMLInputElement) {
      node.value = String(value);
    } else {
      node.setAttribute(key, String(value));
    }
  }
  append(node, children);
  return node;
}

function ariaName(key: string): string {
  return key.startsWith("aria-") ? key : `aria-${key}`;
}

export function append(parent: Node, children: readonly Child[]): void {
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    parent.appendChild(typeof child === "string" ? document.createTextNode(child) : child);
  }
}

/** Replace everything inside a node. `textContent = ""` never parses anything. */
export function clear(node: Element): void {
  node.textContent = "";
}

export function text(tag: keyof HTMLElementTagNameMap, cls: string, value: string): HTMLElement {
  return el(tag, { class: cls }, [value]);
}

/** One shape of an icon. `tag` and `attrs` are our own constants, never data. */
export interface IconShape {
  readonly tag: "path" | "circle" | "line" | "rect" | "polygon" | "polyline";
  readonly attrs: Readonly<Record<string, string>>;
}

/**
 * SVG, built node by node (UX spec §12.1: Lucide, ISC, one set, 24-grid).
 *
 * Stroke, fill, width and caps come from the `.ic` class in the base sheet, so
 * the icon inherits `currentColor` and every slot looks the same everywhere.
 * The control carries the accessible name; the glyph is always `aria-hidden`.
 */
export function icon(name: string, size: number, shapes: readonly IconShape[]): SVGElement {
  const ns = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(ns, "svg");
  svg.setAttribute("class", "ic");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("width", String(size));
  svg.setAttribute("height", String(size));
  svg.setAttribute("data-icon", name);
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("focusable", "false");
  for (const shape of shapes) {
    const node = document.createElementNS(ns, shape.tag);
    for (const [key, value] of Object.entries(shape.attrs)) node.setAttribute(key, value);
    svg.appendChild(node);
  }
  return svg;
}
